import { Link } from "wouter";
import { SEOHead, SOFTWARE_SCHEMA } from "@/components/seo-head";
import { Button } from "@/components/ui/button";
import { SeoPageHeader } from "@/components/seo-page-header";
import {
  ChevronRight,
  CheckCircle2,
  Camera,
  Home,
  Wrench,
  Eye,
  PoundSterling,
  ArrowRight,
} from "lucide-react";

const PAGE_URL = "https://thepropertool.com/quote-from-photo";

const SOFTWARE_APP_SCHEMA = {
  ...SOFTWARE_SCHEMA,
  "name": "The Proper Tool | Quote from a Photo: AI Photo Quoting for UK Builders",
  "description": "Upload site photos and get a fully-priced construction quote in under 10 minutes. The AI identifies scope, materials, and complexity from what it sees. Built for UK builders.",
  "url": PAGE_URL,
};

const BREADCRUMB_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://thepropertool.com/" },
    { "@type": "ListItem", "position": 2, "name": "AI Quoting Software", "item": "https://thepropertool.com/ai-quoting-software" },
    { "@type": "ListItem", "position": 3, "name": "Quote from Photo", "item": PAGE_URL },
  ],
};

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "Can I quote from a photo?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes. The Proper Tool lets you upload site photos alongside a brief description of the job, and the AI uses what it sees to identify scope, materials, existing conditions, and build complexity. It then prices every element from the UK cost library and returns a fully itemised quote you can review and send.",
      },
    },
    {
      "@type": "Question",
      "name": "What kind of photos work best?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Clear, well-lit photos of the work area give the best results. Exterior photos for extensions and loft conversions, internal photos for refurbishments, and close-ups of existing conditions all help the AI identify scope. The more context you provide, such as photos alongside a written description, the more accurate the quote.",
      },
    },
    {
      "@type": "Question",
      "name": "How does the AI price a job from photos?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "The Proper Tool's AI analyses the photos and your description together. It identifies the type of work (extension, loft conversion, refurbishment), the existing materials, conditions, and likely scope. It then maps those observations to the UK cost library (519 tasks, 31 trade categories) and builds a priced, itemised quote. You can edit any line before sending.",
      },
    },
    {
      "@type": "Question",
      "name": "Do I need drawings to use photo quoting?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "No. Photo quoting is designed for situations where you don't yet have architect's drawings, including early-stage site visits, reactive maintenance quotes, or smaller jobs where formal plans aren't needed. If you do have drawings, you can upload those too and the AI will use both sets of inputs.",
      },
    },
    {
      "@type": "Question",
      "name": "Is The Proper Tool free to try?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes. The Proper Tool offers a 14-day free trial with one shared allowance of 5 total quote creations across Auto, Template, and Manual quotes and full access to photo quoting and all other features. No credit card is required to start. After the trial, plans start from £29/month for sole traders.",
      },
    },
  ],
};

const FAQS = [
  {
    q: "Can I quote from a photo?",
    a: "Yes. The Proper Tool lets you upload site photos alongside a brief description of the job, and the AI uses what it sees to identify scope, materials, existing conditions, and build complexity. It then prices every element from the UK cost library and returns a fully itemised quote you can review and send.",
  },
  {
    q: "What kind of photos work best?",
    a: "Clear, well-lit photos of the work area give the best results. Exterior photos for extensions and loft conversions, internal photos for refurbishments, and close-ups of existing conditions all help the AI identify scope. The more context you provide, such as photos alongside a written description, the more accurate the quote.",
  },
  {
    q: "How does the AI price a job from photos?",
    a: "The Proper Tool's AI analyses the photos and your description together. It identifies the type of work (extension, loft conversion, refurbishment), the existing materials, conditions, and likely scope. It then maps those observations to the UK cost library (519 tasks, 31 trade categories) and builds a priced, itemised quote. You can edit any line before sending.",
  },
  {
    q: "Do I need drawings to use photo quoting?",
    a: "No. Photo quoting is designed for situations where you don't yet have architect's drawings, including early-stage site visits, reactive maintenance quotes, or smaller jobs where formal plans aren't needed. If you do have drawings, you can upload those too and the AI will use both sets of inputs.",
  },
  {
    q: "Is The Proper Tool free to try?",
    a: "Yes. The Proper Tool offers a 14-day free trial with one shared allowance of 5 total quote creations across Auto, Template, and Manual quotes and full access to photo quoting and all other features. No credit card is required to start. After the trial, plans start from £29/month for sole traders.",
  },
];

const USE_CASES = [
  {
    icon: Home,
    title: "Extensions and loft conversions",
    description: "Photograph the existing rear elevation, the garden, and the loft hatch. The AI assesses the footprint, roof pitch, and access conditions to scope the work.",
  },
  {
    icon: Wrench,
    title: "Refurbishments and fit-outs",
    description: "Capture the existing room conditions: substrate, services, fixtures. The AI identifies what needs stripping out and what can be retained.",
  },
  {
    icon: Eye,
    title: "Early-stage site visits",
    description: "No drawings yet? Take photos on your first visit. Get a ballpark quote back before the architect is even appointed, useful for initial client conversations.",
  },
  {
    icon: Camera,
    title: "Reactive and maintenance jobs",
    description: "Photo a damaged render panel, a leaking flat roof, or a blocked drain. The AI scopes the repair, identifies the trade, and prices it from UK cost library rates.",
  },
];

const RELATED_PAGES = [
  { href: "/ai-quoting-software/", label: "AI Quoting Software" },
  { href: "/quote-from-drawings/", label: "Quote from Drawings" },
  { href: "/auto-quote/", label: "Auto Quote" },
  { href: "/ai-takeoff-software/", label: "AI Takeoff Software" },
  { href: "/quote-from-description/", label: "Quote from a Description" },
  { href: "/ai-construction-estimating-software/", label: "AI Construction Estimating" },
  { href: "/quoting-software-for-builders/", label: "Quoting Software for Builders" },
  { href: "/construction-estimating-software/", label: "Construction Estimating Software" },
  { href: "/estimating-software-for-builders/", label: "Estimating Software for Builders" },
  { href: "/pricing/", label: "Pricing" },
  { href: "/free-trial/", label: "Free Trial" },
];

export default function QuoteFromPhotoPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEOHead
        title="Quote from a Photo: AI Photo Quoting for UK Builders | The Proper Tool"
        description="Upload site photos and get a fully-priced construction quote in under 10 minutes. The AI identifies scope, materials, and complexity from what it sees. Built for UK builders."
        canonical="/quote-from-photo"
        schema={[SOFTWARE_APP_SCHEMA, BREADCRUMB_SCHEMA, FAQ_SCHEMA]}
      />

      <SeoPageHeader />

      <main>
        {/* Hero */}
        <section className="py-16 sm:py-24 bg-gradient-to-b from-primary/5 to-background">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6 justify-center flex-wrap">
              <Link href="/" className="hover:text-foreground transition-colors">Home</Link>
              <ChevronRight className="h-4 w-4" />
              <Link href="/ai-quoting-software" className="hover:text-foreground transition-colors">AI Quoting Software</Link>
              <ChevronRight className="h-4 w-4" />
              <span>Quote from Photo</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight tracking-tight mb-6">
              Quote from a photo
            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed mb-8 max-w-3xl mx-auto">
              Take photos on site, upload them to The Proper Tool, and get a fully-priced, itemised quote in under 10 minutes. No drawings needed. The AI reads what it sees and prices the job.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button size="lg" asChild>
                <Link href="/login">
                  Start free, no card needed
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link href="/pricing/">See pricing</Link>
              </Button>
            </div>
            <p className="text-sm text-muted-foreground mt-4">14-day free trial · 5 total quote creations across Auto, Template, and Manual quotes · No credit card required</p>
          </div>
        </section>

        {/* How it works */}
        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">How photo quoting works</h2>
            <p className="text-lg text-muted-foreground leading-relaxed mb-4">
              Most quoting tools need drawings. Most builders don't always have them, especially on the first site visit, for reactive jobs, or for smaller works where an architect hasn't been appointed.
            </p>
            <p className="text-lg text-muted-foreground leading-relaxed mb-4">
              The Proper Tool's AI is trained to identify construction scope from photos. It reads the images you upload alongside your description of the job, recognising materials, existing conditions, build type, and complexity, then maps what it sees to the UK cost library and returns a fully priced, itemised quote.
            </p>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Every line is editable. Adjust quantities, swap rates, add items, or delete anything the AI included that doesn't apply. Then export as a branded PDF.
            </p>
          </div>
        </section>

        {/* Use cases */}
        <section className="py-16 sm:py-20 bg-muted/30 border-t border-border/40">
          <div className="max-w-5xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">Works for every type of job</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                From extensions to reactive repairs: quote from photos wherever you are.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 gap-6">
              {USE_CASES.map((useCase) => (
                <div key={useCase.title} className="bg-background rounded-xl border border-border p-6">
                  <useCase.icon className="h-6 w-6 text-primary mb-3" />
                  <h3 className="font-semibold text-base mb-2">{useCase.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{useCase.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Three steps */}
        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">From photo to quote in three steps</h2>
            <div className="space-y-6">
              {[
                {
                  step: "1",
                  title: "Upload your photos",
                  desc: "Take photos on site, or attach images from your phone, tablet, or laptop. Add a brief description of the job alongside for best results.",
                },
                {
                  step: "2",
                  title: "The AI identifies the scope",
                  desc: "The AI analyses your photos and description together, identifies what work is needed, and maps every element to the UK cost library across all 31 trade categories.",
                },
                {
                  step: "3",
                  title: "Review, edit, and send",
                  desc: "You get a fully itemised, priced quote. Adjust any line, add notes, apply your overhead and profit margin, then export as a branded PDF.",
                },
              ].map(({ step, title, desc }) => (
                <div key={step} className="flex gap-5">
                  <div className="flex-shrink-0 w-9 h-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-sm">
                    {step}
                  </div>
                  <div>
                    <h3 className="font-semibold text-base mb-1">{title}</h3>
                    <p className="text-muted-foreground text-sm leading-relaxed">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* What the AI looks for */}
        <section className="py-16 sm:py-20 bg-muted/30 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">What the AI looks for in your photos</h2>
            <p className="text-lg text-muted-foreground leading-relaxed mb-6">
              The AI reads visual cues that an experienced builder reads instinctively: existing materials, access constraints, condition of the substrate, and what trades are likely to be involved.
            </p>
            <ul className="space-y-3 mb-8">
              {[
                "Build type: extension, loft, refurbishment, new build, reactive repair",
                "Existing materials: brick, block, timber, render, slate, tile, felt",
                "Condition: strip-out required, retain and make good, or full replacement",
                "Access: scaffolding likely, restricted access, multi-storey working",
                "Services present: visible soil stacks, overflow pipes, rooflight positions",
                "Finish level: standard residential, high specification, or commercial grade",
              ].map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-muted-foreground">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Combine with drawings */}
        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">Combine photos with drawings for maximum accuracy</h2>
            <p className="text-lg text-muted-foreground leading-relaxed mb-4">
              Photos give the AI context: the existing conditions, the site, the complexity. Drawings give it precision: the beam sizes, the floor areas, the exact specification. When you have both, upload both.
            </p>
            <p className="text-lg text-muted-foreground leading-relaxed mb-6">
              The AI combines all inputs into a single scope, cross-checks quantities, and produces the most accurate output possible from the information available.
            </p>
            <Link href="/ai-takeoff-software/" className="text-primary font-medium hover:underline flex items-center gap-1">
              <ChevronRight className="h-4 w-4" />
              Learn about AI takeoff from drawings
            </Link>
          </div>
        </section>

        {/* Priced on UK rates */}
        <section className="py-16 sm:py-20 bg-muted/30 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">Every quote priced on real UK construction rates</h2>
            <p className="text-lg text-muted-foreground leading-relaxed mb-4">
              Whatever the AI identifies from your photos, it prices from the same UK cost library used across every quote on The Proper Tool: 519 rated tasks across 31 trade categories.
            </p>
            <ul className="space-y-3">
              {[
                "519 rated tasks across 31 trade categories",
                "Low, Typical, and High rate bands built in",
                "Override any rate with your own figures",
                "Labour, materials, and plant broken out on every line",
                "Professional PDF export with your branding",
              ].map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <PoundSterling className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-muted-foreground">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Pricing callout */}
        <section className="py-16 sm:py-20 border-t border-border/40" id="pricing">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">Simple pricing, free to try</h2>
              <p className="text-lg text-muted-foreground">14-day free trial · 5 total quote creations across Auto, Template, and Manual quotes · No credit card required</p>
            </div>
            <div className="grid sm:grid-cols-3 gap-6 mb-8">
              {([
                { name: "Lite", price: "£29", desc: "For sole traders", features: ["10 AI auto-quotes/mo", "Unlimited template & manual quotes", "Professional PDF export", "Full 519-item cost library"], highlight: false },
                { name: "Starter", price: "£59", desc: "For growing businesses", features: ["25 AI auto-quotes/mo", "Everything in Lite", "Pricing Accuracy Index", "Expert validation pipeline", "Regional pricing"], highlight: true },
                { name: "Pro", price: "£99", desc: "For high-volume builders", features: ["50 AI auto-quotes/mo", "Everything in Starter", "Drawing analysis", "Learned rate adjustments"], highlight: false },
              ] as { name: string; price: string; desc: string; features: string[]; highlight: boolean }[]).map((plan) => (
                <div
                  key={plan.name}
                  className={`rounded-xl border p-6 flex flex-col ${plan.highlight ? "border-primary bg-primary/5" : "border-border bg-background"}`}
                >
                  <div className="mb-4">
                    <p className="font-bold text-lg">{plan.name}</p>
                    <p className="text-3xl font-bold mt-1">{plan.price}<span className="text-base font-normal text-muted-foreground">/mo + VAT</span></p>
                    <p className="text-sm text-muted-foreground mt-1">{plan.desc}</p>
                  </div>
                  <ul className="space-y-2 flex-1 mb-6">
                    {plan.features.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm">
                        <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Button variant={plan.highlight ? "default" : "outline"} asChild className="w-full">
                    <Link href="/login">Start free trial</Link>
                  </Button>
                </div>
              ))}
            </div>
            <div className="text-center space-x-4 text-sm">
              <Link href="/pricing/" className="text-primary font-medium hover:underline">Full pricing details</Link>
              <span className="text-muted-foreground">·</span>
              <Link href="/free-trial/" className="text-primary font-medium hover:underline">About the free trial</Link>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 sm:py-20 bg-muted/30 border-t border-border/40">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-10 text-center">Frequently asked questions</h2>
            <div className="space-y-8">
              {FAQS.map((faq) => (
                <div key={faq.q} className="border-b border-border/60 pb-8 last:border-0 last:pb-0">
                  <h3 className="font-semibold text-lg mb-3">{faq.q}</h3>
                  <p className="text-muted-foreground leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Internal links */}
        <section className="py-12 border-t border-border/40 bg-muted/20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-xl font-semibold mb-6">Related pages</h2>
            <div className="grid sm:grid-cols-3 gap-4">
              {RELATED_PAGES.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="flex items-start gap-2 text-sm text-primary hover:underline font-medium"
                >
                  <ChevronRight className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Ready to quote on the spot?</h2>
            <p className="text-lg text-muted-foreground mb-8">
              Start your free 14-day trial. 5 total quote creations across Auto, Template, and Manual quotes included. No credit card required.
            </p>
            <Button size="lg" asChild>
              <Link href="/login">
                Start your free trial
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <p className="text-sm text-muted-foreground mt-4">No credit card required · Cancel any time</p>
          </div>
        </section>
      </main>

      <footer className="border-t border-border/40 py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
          <p>© {new Date().getFullYear()} The Proper Tool. Built for builders.</p>
          <div className="flex gap-6">
            <Link href="/privacy" className="hover:text-foreground transition-colors">Privacy</Link>
            <Link href="/terms" className="hover:text-foreground transition-colors">Terms</Link>
            <Link href="/blog" className="hover:text-foreground transition-colors">Blog &amp; Guides</Link>
            <Link href="/comparisons" className="hover:text-foreground transition-colors">Compare</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
